import { selectFeatureFlags } from 'core/userContext';
import { useBillingQueryContext } from 'modules/billing/contexts';
import { useSelector } from 'react-redux';
import {
  EMAILS_SENT,
  IN_APP_NOTIFICATIONS_SENT,
  MESSAGING_METRICS,
  MMS_ATTACHMENTS_RECEIVED,
  MMS_ATTACHMENTS_SENT,
  MMS_ATTACHMENTS_TOTAL,
  PUSH_NOTIFICATIONS_SENT,
  SMS_SEGMENTS_RECEIVED,
  SMS_SEGMENTS_SENT,
  SMS_SEGMENTS_TOTAL,
  SMS_SENT,
  SUCCESSFUL_SMS_VERIFICATIONS,
  WEB_PUSH_NOTIFICATIONS_SENT,
  WHATSAPP_SENT,
} from '../../../constants';
import { Metric } from '../../../types';
import {
  billingPartyText,
  isOrgLevelSmsMetric,
  isOrgLevelWhatsAppMetric,
  isProviderMetric,
} from '../../../utils';
import { CollapsibleMetricRow } from '../CollapsibleMetricRow';
import { LoadingCard } from '../LoadingCard';
import { MetricRow } from '../MetricRow';
import { MetricTile } from '../MetricTile';
import { ProjectBreakdownLink } from '../ProjectBreakdownLink';
import {
  Container,
  Header,
  MainContent,
  Marquee,
  RowsWrapper,
  TableHeading,
  TableHeadingRow,
  Title,
} from '../styled';

const accumulateByBillingParty = (acc: Record<string, number>, metric: Metric) => {
  const label = billingPartyText(metric);
  return !acc[label]
    ? { ...acc, [label]: metric.value }
    : { ...acc, [label]: acc[label] + metric.value };
};

export const MessagingCard = () => {
  const { pickOrgValue, pickOrgMetrics, isEnabledByProvider, isLoading } = useBillingQueryContext();
  const {
    BillingDisplayBccCcEmailsUI,
    ExpandedChannelMetrics,
    ShowSmsMmsBillingTotalCounts,
    WhatsAppMessageChannelGA,
  } = useSelector(selectFeatureFlags);

  /**
   * Messaging metrics are different from metrics in other categories (Custom Events,
   * Users, Catalog) in that distinct Org-level metrics are provided at a few different
   * aggregation levels. ie. For SmsSegmentsSent, the Usage BE endpoint would return an
   * org-level metric for the grand total, and then a total for each provider (for Billing
   * Party/Provider Breakdowns) then a total for each country as well (for GlobalSMS).
   * Therefore we need to extend pickValue and pickMetrics with filters that narrow the
   * results to only those required for each UI element.
   */
  const pickValue = (metricName: string) => pickOrgValue(metricName, isOrgLevelSmsMetric);

  const pickMetrics = (metricName: string) => pickOrgMetrics(metricName, isProviderMetric);

  const pickBillingPartyRows = (metricName: string) => {
    const metrics = pickMetrics(metricName) || [];
    const byBillingParty = metrics?.reduce(accumulateByBillingParty, {} as Record<string, number>);
    return Object.entries(byBillingParty)
      .map(([label, value]) => ({ label, value }))
      .filter((row) => isEnabledByProvider[row.label as keyof typeof isEnabledByProvider]);
  };

  const emailsSentValue = pickOrgValue(EMAILS_SENT);
  const inAppNotificationsSentValue = pickOrgValue(IN_APP_NOTIFICATIONS_SENT);
  const mmsAttachmentsTotalValue = pickOrgValue(MMS_ATTACHMENTS_TOTAL, isOrgLevelSmsMetric);
  const pushNotificationsSentValue = pickOrgValue(PUSH_NOTIFICATIONS_SENT);
  const smsSegmentsTotal = pickOrgValue(SMS_SEGMENTS_TOTAL, isOrgLevelSmsMetric);
  const smsSentValue = pickOrgValue(SMS_SENT, isOrgLevelSmsMetric);
  const webPushNotificationsSentValue = pickOrgValue(WEB_PUSH_NOTIFICATIONS_SENT);
  const whatsAppSentValue = pickOrgValue(WHATSAPP_SENT, isOrgLevelWhatsAppMetric);

  const smsContentModernized = {
    label: 'SMS & MMS total',
    tooltip: 'Includes SMS segments and MMS, sent and received.',
    value: smsSegmentsTotal + mmsAttachmentsTotalValue,
  };

  const smsContentLegacy = {
    label: 'SMS & MMS sent',
    tooltip: 'Includes both SMS and MMS sent',
    value: smsSentValue,
  };

  const smsContent = ShowSmsMmsBillingTotalCounts ? smsContentModernized : smsContentLegacy;

  return isLoading ? (
    <LoadingCard />
  ) : (
    <Container data-test="messaging-metrics">
      <Header>
        <Title>Messaging</Title>
        <div>
          <ProjectBreakdownLink to="/payments/info/messagingUsage" />
        </div>
      </Header>
      <Marquee>
        <MetricTile label="Emails sent" value={emailsSentValue} />
        <MetricTile
          label={smsContent.label}
          tooltip={smsContent.tooltip}
          value={smsContent.value}
        />
        <MetricTile
          label="Push sent"
          tooltip="Number of mobile pushes sent within the timeframe."
          value={pushNotificationsSentValue}
        />
        {ExpandedChannelMetrics && (
          <>
            <MetricTile label="Web push sent" value={webPushNotificationsSentValue} />
            <MetricTile label="In-app sent" value={inAppNotificationsSentValue} />
          </>
        )}
        {WhatsAppMessageChannelGA && <MetricTile label="WhatsApp sent" value={whatsAppSentValue} />}
      </Marquee>
      <MainContent>
        {BillingDisplayBccCcEmailsUI && (
          <>
            <TableHeadingRow>
              <TableHeading>Email</TableHeading>
            </TableHeadingRow>
            <RowsWrapper>
              <MetricRow label="To sends" value={pickValue(MESSAGING_METRICS.emailsSent)} />
              <MetricRow label="CC sends" value={pickValue(MESSAGING_METRICS.ccEmailsSent)} />
              <MetricRow label="BCC sends" value={pickValue(MESSAGING_METRICS.bccEmailsSent)} />
            </RowsWrapper>
          </>
        )}

        <TableHeadingRow>
          <TableHeading>SMS</TableHeading>
        </TableHeadingRow>
        <RowsWrapper>
          <CollapsibleMetricRow
            label="SMS segments sent"
            tooltip="Total segments of SMS sent from all SMS providers from all projects"
            value={pickValue(SMS_SEGMENTS_SENT)}
            rows={pickBillingPartyRows(SMS_SEGMENTS_SENT)}
          />
          <CollapsibleMetricRow
            label="SMS segments received"
            value={pickValue(SMS_SEGMENTS_RECEIVED)}
            rows={pickBillingPartyRows(SMS_SEGMENTS_RECEIVED)}
          />
          <CollapsibleMetricRow
            label="SMS segments total"
            value={pickValue(SMS_SEGMENTS_TOTAL)}
            rows={pickBillingPartyRows(SMS_SEGMENTS_TOTAL)}
          />
          <CollapsibleMetricRow
            label="MMS sent"
            value={pickValue(MMS_ATTACHMENTS_SENT)}
            rows={pickBillingPartyRows(MMS_ATTACHMENTS_SENT)}
          />
          <CollapsibleMetricRow
            label="MMS received"
            value={pickValue(MMS_ATTACHMENTS_RECEIVED)}
            rows={pickBillingPartyRows(MMS_ATTACHMENTS_RECEIVED)}
          />
          <CollapsibleMetricRow
            label="MMS total"
            value={pickValue(MMS_ATTACHMENTS_TOTAL)}
            rows={pickBillingPartyRows(MMS_ATTACHMENTS_TOTAL)}
          />
          <CollapsibleMetricRow
            label="SMS verifications"
            tooltip="Number of verification codes sent via SMS that were verified successfully"
            value={pickValue(SUCCESSFUL_SMS_VERIFICATIONS)}
            rows={pickBillingPartyRows(SUCCESSFUL_SMS_VERIFICATIONS)}
            last={!ShowSmsMmsBillingTotalCounts}
          />
          {ShowSmsMmsBillingTotalCounts && (
            <MetricRow
              label={smsContentLegacy.label.concat(' (Legacy)')}
              tooltip={smsContentLegacy.tooltip}
              value={smsContentLegacy.value}
            />
          )}
        </RowsWrapper>
      </MainContent>
    </Container>
  );
};
